import { useEffect } from "react";

import { DataFilter } from "interfaces";

import { formatValue } from "utils/formatting";

import Button from "../Button";
import ToolTip from "../Tooltip";
import InputField from "../Input-Field";
import InputRange from "../Input-Range";

import { DataFilterProps } from "./types";
import {
  isArray,
  getUniqueValues,
  getOperatorOptions,
} from "./operator-options";
import useFilterForm from "./form";

const FilterComponent = ({
  name = "",
  columns,
  columnTypes,
  stats,
  onColumnSelect,
  addFilter,
  showFilterHelp = true,
  initFilter,
}: DataFilterProps) => {
  const {
    column,
    updateColumn,
    condition,
    updateCondition,
    operator,
    updateOperator,
    range,
    updateRange,
    errors,
    onSubmit,
  } = useFilterForm(columnTypes, initFilter, addFilter, onColumnSelect);

  useEffect(() => {
    if (columns.length === 1 && columns[0] !== column) {
      updateColumn(columns[0]);
    }
  }, [columns, column, updateColumn]);

  const operatorOptions = column
    ? getOperatorOptions(columnTypes[column], stats[column])
    : [];

  const colStats = stats?.[column];

  return (
    <>
      <div className="m-1 py-1">
        <InputField
          name={`${name}column`}
          type="text"
          label="Column to Filter on"
          allowedValues={columns}
          required
          value={column || null}
          error={errors.column}
          onChange={updateColumn}
          placeholder="Select Column to Filter On"
          help={
            column &&
            colStats && (
              <>
                <p>
                  <span>{colStats.count?.toLocaleString()} valid values.</span>
                  {columnTypes[column] !== "char" ? (
                    <span>
                      {" "}
                      Data lies between{" "}
                      {formatValue(colStats.min, columnTypes[column])} and{" "}
                      {formatValue(colStats.max, columnTypes[column])}.{" "}
                      {colStats["2.5%"] !== undefined &&
                        colStats["97.5%"] !== undefined && (
                          <span>
                            95% of the data lies between{" "}
                            {formatValue(colStats["2.5%"], columnTypes[column])}{" "}
                            and{" "}
                            {formatValue(
                              colStats["97.5%"],
                              columnTypes[column]
                            )}
                          </span>
                        )}
                      .
                    </span>
                  ) : (
                    <span>
                      {" "}
                      {colStats?.["unique-count"].toLocaleString()} unique
                      values
                      {(colStats?.["unique-values"].length || 0) ===
                        colStats?.["unique-count"] && (
                        <span>
                          :{" "}
                          <span className="italic">
                            {colStats["unique-values"].join(", ")}
                          </span>
                        </span>
                      )}
                      .
                    </span>
                  )}
                </p>
              </>
            )
          }
        />
      </div>
      <div className="m-1 py-1">
        <InputField
          name={`${name}condition`}
          type="text"
          label="Filter Condition: Exclude/Include"
          options={[
            { value: "exclude", label: "Exclude ALL values in range" },
            { value: "include", label: "Include ANY values in range" },
          ]}
          required
          value={condition}
          error={errors.condition}
          onChange={updateCondition}
        />
      </div>
      {column && condition && (
        <div className="m-1 py-1">
          <InputField<DataFilter.Operator>
            name={`${name}operator`}
            type="text"
            label="Operator"
            options={operatorOptions}
            required
            value={operator || null}
            error={errors.operator}
            onChange={updateOperator}
            placeholder="Select Operator"
          />
        </div>
      )}
      {column && condition && operator && (
        <div className="m-1 py-1">
          {columnTypes[column] === "char" && (
            <InputRange
              name={`${name}range`}
              label="Filter Range"
              type="char"
              range={range as string[]}
              error={errors.range}
              onValueChange={(r) => updateRange(r || [])}
              allowedValues={
                stats?.[column]
                  ? (getUniqueValues(stats?.[column]) as string[]).length === 0
                    ? undefined
                    : (getUniqueValues(stats?.[column]) as string[])
                  : undefined
              }
              required
              placeholder="Select Values"
            />
          )}
          {["int", "float", "numeric"].includes(columnTypes[column]) && (
            <InputRange
              name={`${name}range`}
              label="Filter Range"
              type="numeric"
              range={range as [number, number]}
              error={errors.range}
              // @ts-ignore
              onValueChange={updateRange}
              hideUpper={!isArray(columnTypes[column], operator)}
              required
              help={
                colStats ? (
                  <p className="text-sm pt-1">
                    Data for {column} lies between{" "}
                    {colStats.min?.toLocaleString()} and{" "}
                    {colStats.max?.toLocaleString()}.{" "}
                    {colStats["2.5%"] !== undefined &&
                      colStats["97.5%"] !== undefined && (
                        <span>
                          95% of the data lies between{" "}
                          {colStats["2.5%"].toLocaleString()} and{" "}
                          {colStats["97.5%"].toLocaleString()}
                        </span>
                      )}
                    .
                  </p>
                ) : undefined
              }
            />
          )}
          {["date", "time"].includes(columnTypes[column]) && (
            <InputRange
              name={`${name}range`}
              label="Filter Range"
              type="date"
              range={range as [Date, Date]}
              error={errors.range}
              // @ts-ignore
              onValueChange={updateRange}
              hideUpper={!isArray(columnTypes[column], operator)}
              required
              help={
                colStats ? (
                  <p className="text-sm pt-1">
                    Data for {column} lies between{" "}
                    {formatValue(colStats.min, "date")} and{" "}
                    {formatValue(colStats.max, "date")}.{" "}
                    {colStats["2.5%"] !== undefined &&
                      colStats["97.5%"] !== undefined && (
                        <span>
                          95% of the data lies between{" "}
                          {formatValue(colStats["2.5%"], "date")} and{" "}
                          {formatValue(colStats["97.5%"], "date")}
                        </span>
                      )}
                    .
                  </p>
                ) : undefined
              }
            />
          )}
          {showFilterHelp ? (
            <ToolTip
              tooltipText={
                <p className={"m-1 p-2"}>
                  A filter is a set of conditions. Each condition has a
                  VARIABLE, a CONDITION (include/exclude), an OPERATOR (in,
                  {`>=`}, etc.), and value range. ALL conditions must be met for
                  a filter to be applied
                </p>
              }
            >
              <Button
                onClick={onSubmit}
                disabled={
                  !(column && condition && operator && range.length >= 1)
                }
              >
                {initFilter && initFilter.column === column ? "Edit" : "Add"}{" "}
                Filter{" "}
              </Button>
            </ToolTip>
          ) : (
            <div>
              <Button
                type="submit"
                disabled={
                  !(column && condition && operator && range.length >= 1)
                }
              >
                {initFilter && initFilter.column === column ? "Edit" : "Add"}{" "}
                Filter{" "}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default FilterComponent;
